import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import { sendImpressions } from 'reaxl-analytics';
import { esntialEligibilityFetcher } from 'reaxl-ecommerce';

import getEsntialEligibilityConfig from '@/utilities/getEsntialEligibilityConfig.js';

import inventoryDuck from '@/ducks/inventoryDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

export default function useVdpEsntialEligibility(isSponsoredExperience) {
    const [isEligible, setIsEligible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const dispatch = useDispatch();
    const {
        esntial_eligibility_v2: [enableV2],
    } = useFeatures(['esntial_eligibility_v2']);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const { esntial, id, vin } = listing;

    const existingDeal = useSelector((state) => paymentsDuck?.selectors?.getDealByListingVin(state, vin)) || {};

    const disableEsntial = useCallback(() => {
        dispatch(inventoryDuck.creators.updateInventoryItem(id, {
            esntial: undefined,
            ecommWebsite: undefined,
        }));
        setIsEligible(false);
    }, [dispatch, id]);

    useEffect(() => {
        if (vin && !isLoading) {
            sendImpressions({ name: 'dealerSegmentationImpression' });
        }
    }, [vin, isLoading]);

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        disableEsntial();

        if (!listing || !esntial?.ecommWebsite) {
            setIsEligible(false);
            setIsLoading(false);
            return;
        }

        // Create payload and endpoint configuration using utility function
        const { payloadItems, eligibilityURL, isV2Enabled } = getEsntialEligibilityConfig(listing, enableV2);

        // safeguard if eligibilityURL is undefined
        if (!eligibilityURL) {
            setIsEligible(false);
            setIsLoading(false);
            return;
        }

        esntialEligibilityFetcher(eligibilityURL, isV2Enabled ? payloadItems : vin)
            .then((response) => {
                if (response?.totalMatched >= 1 || response?.[0]?.sellable) {
                    // Enable ecommWebsite and dealerPreferredCTA to show
                    // Esntial entry points
                    dispatch(inventoryDuck.creators.updateInventoryItem(id, {
                        esntial,
                        ecommWebsite: esntial.ecommWebsite,
                        owner: {
                            ...listing.owner,
                            dealerPreferredCTA: esntial.deemphasized ? undefined : 'WEBSITE',
                            ...(isSponsoredExperience && { hideEmailForm: true }),
                        },
                    }));
                    setIsEligible(true);

                    if (window && window.newrelic) {
                        if (isV2Enabled) {
                            window.newrelic.addPageAction('ThirdPartyAction', {
                                id,
                                payloadItems,
                                component: 'EsntialEligibiltyContainer',
                                message: 'Vehicle eligible for Esntial V2',
                            });
                        } else {
                            window.newrelic.addPageAction('ThirdPartyAction', {
                                id,
                                vin,
                                component: 'EsntialEligibiltyContainer',
                                message: 'Vehicle eligible for Esntial',
                            });
                        }
                    }
                } else {
                    let message = 'Vehicle ineligible for Esntial';
                    if (response?.circuitBreakerOpen) {
                        message = 'Esntial Eligibility Circuit Breaker Open; displaying as vehicle ineligible for Esntial';
                    }
                    // eslint-disable-next-line no-console
                    console.log(message, vin);
                    if (window && window.newrelic) {
                        if (isV2Enabled) {
                            window.newrelic.addPageAction('ThirdPartyError', {
                                id,
                                payloadItems,
                                component: 'EsntialEligibiltyContainer',
                                message: message + ' V2',
                            });
                        } else {
                            window.newrelic.addPageAction('ThirdPartyError', {
                                id,
                                vin,
                                component: 'EsntialEligibiltyContainer',
                                message,
                            });
                        }
                    }
                }
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        vin,
        existingDeal.isEcommerce,
    ]);

    return { isEligible, isLoading, error };
}
